import {
  ArrowLeftOutlined,
  CloseOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Space } from 'antd';
import { useStore } from 'zustand';

import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';

import { inventoryStore } from '../../../../InventoryStore';
import { formatExperienceName } from '../../../../_components/TabViews/ExperiencesTab/_components/utils';
import styles from '../../TimeslotsModal.module.scss';

type ActivitiesSelectorProps = {
  activities: ExtranetHotelExperience[];
};

const ActivitiesSelector = ({ activities }: ActivitiesSelectorProps) => {
  const { data: experiencesCategories } = useExperiencesCategories();

  const selectedActivityId = useStore(
    inventoryStore,
    (state) => state.timeslotsModal.selectedActivityId
  );
  const setTimeslotsSelectedActivityId = useStore(
    inventoryStore,
    (state) => state.setTimeslotsSelectedActivityId
  );

  const items = activities.reduce<NonNullable<MenuProps['items']>>(
    (acc, activity) => {
      const categoryName = experiencesCategories?.find(
        (cat) => cat.id === activity.categoryId
      )?.name;

      const name = formatExperienceName(activity, categoryName);

      if (activity.id !== selectedActivityId) {
        acc.push({ label: name, key: String(activity.id) });
      }

      return acc;
    },
    []
  );

  const selectedActivity = activities.find(
    (activity) => activity.id === selectedActivityId
  );

  const name = selectedActivity
    ? formatExperienceName(
        selectedActivity,
        experiencesCategories?.find(
          (cat) => cat.id === selectedActivity.categoryId
        )?.name
      )
    : '';

  const handleSelect: MenuProps['onClick'] = ({ key }) => {
    setTimeslotsSelectedActivityId(Number(key));
  };

  if (items.length === 0) {
    return <span className={styles.activityName}>{name}</span>;
  }

  return (
    <Dropdown
      menu={{
        items,
        onClick: handleSelect,
        selectedKeys: [String(selectedActivityId)],
      }}
      className={styles.activityNameButton}
      trigger={['click']}
    >
      <span className={styles.activityName}>
        <Space size={8}>
          {name}
          <DownOutlined />
        </Space>
      </span>
    </Dropdown>
  );
};

type HeaderProps = {
  onClose: () => void;
  activities: ExtranetHotelExperience[];
};

export const Header = ({ onClose, activities }: HeaderProps) => {
  return (
    <div className={styles.timeslotsModalHeader}>
      <div className={styles.left}>
        <div className={styles.backButton}>
          <Button icon={<ArrowLeftOutlined />} onClick={onClose} />
        </div>
        <ActivitiesSelector activities={activities} />
      </div>
      <div className={styles.right}>
        <div className={styles.closeButton}>
          <Button icon={<CloseOutlined />} onClick={onClose} />
        </div>
      </div>
    </div>
  );
};
